/**
 * Compliance Rule Modal
 * Create/edit compliance monitoring rules
 */

import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import useFocusTrap from '../../hooks/useFocusTrap';

const RULE_TYPES = ['policy', 'script', 'regulatory', 'data'];
const SEVERITY_LEVELS = ['high', 'medium', 'low'];

const INITIAL_FORM = {
  rule_name: '',
  description: '',
  rule_type: 'policy',
  severity: 'medium',
  detection_keywords: '',
  is_active: true,
};

const ComplianceRuleModal = ({ isOpen, onClose, onSave, rule }) => {
  const isEdit = !!rule;
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);

  useFocusTrap(modalRef);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    if (rule) {
      setForm({
        rule_name: rule.rule_name || '',
        description: rule.description || '',
        rule_type: rule.rule_type || 'policy',
        severity: rule.severity || 'medium',
        detection_keywords: Array.isArray(rule.detection_keywords) ? rule.detection_keywords.join(', ') : (rule.detection_keywords || ''),
        is_active: rule.is_active ?? true,
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [rule]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rule_name.trim()) return;
    const payload = {
      ...form,
      detection_keywords: form.detection_keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean),
    };
    setSaving(true);
    await onSave(payload, rule?.id);
    setSaving(false);
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit Compliance Rule' : 'Add Compliance Rule'}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{isEdit ? 'Edit Compliance Rule' : 'Add Compliance Rule'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
            <input type="text" className="input text-sm" value={form.rule_name} onChange={(e) => handleChange('rule_name', e.target.value)} maxLength={100} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input text-sm" rows={2} value={form.description} onChange={(e) => handleChange('description', e.target.value)} maxLength={500} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
              <select className="input text-sm" value={form.rule_type} onChange={(e) => handleChange('rule_type', e.target.value)}>
                {RULE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select className="input text-sm" value={form.severity} onChange={(e) => handleChange('severity', e.target.value)}>
                {SEVERITY_LEVELS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Detection Keywords</label>
            <input type="text" className="input text-sm" placeholder="keyword1, keyword2, keyword3" value={form.detection_keywords} onChange={(e) => handleChange('detection_keywords', e.target.value)} maxLength={500} />
            <p className="text-xs text-gray-400 mt-1">Comma-separated keywords to detect in interactions</p>
          </div>
          {isEdit && (
            <div className="flex items-center space-x-2">
              <input type="checkbox" id="comp_rule_active" checked={form.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} className="rounded border-gray-300" />
              <label htmlFor="comp_rule_active" className="text-sm text-gray-700">Active</label>
            </div>
          )}
          <div className="flex items-center justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-sm px-4 py-2 disabled:opacity-50">
              {saving ? 'Saving...' : isEdit ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplianceRuleModal;
